import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { browseRecipes } from '../features/careMenu/mealDb'
import { localCareMenuRepository, subscribeToCareMenu } from '../features/careMenu/repository'
import type { RecipeSummary } from '../features/careMenu/types'

const demoRecipe: RecipeSummary = {
  id: 'fallback-pasta',
  name: 'Tomato pasta',
  thumbnail: '',
  category: 'Everyday',
}

function todayISO() {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function TodayDinnerCard({ ownerId, eyebrow, compact = false, personal = false }: { ownerId: 'u-alex' | 'u-lillian'; eyebrow: string; compact?: boolean; personal?: boolean }) {
  const [meal, setMeal] = useState<RecipeSummary>(demoRecipe)
  const [assigned, setAssigned] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    const refresh = async () => {
      const data = await localCareMenuRepository.read(ownerId)
      const assignment = data.assignments[todayISO()]
      if (!active) return
      if (assignment) {
        setMeal(assignment.meal)
        setAssigned(true)
        setLoading(false)
        return
      }
      setAssigned(false)
      const result = await browseRecipes()
      if (!active) return
      setMeal(result.recipes[0] ?? demoRecipe)
      setLoading(false)
    }
    void refresh()
    const unsubscribe = subscribeToCareMenu(ownerId, () => void refresh())
    return () => { active = false; unsubscribe() }
  }, [ownerId])

  return <Link to="/care-menu" aria-label={`Open ${meal.name} in Weekly Menu and Recipes`} className={`block overflow-hidden border border-[#bcd2e8] bg-white shadow-[0_6px_22px_rgba(35,78,126,.06)] transition active:scale-[.995] ${compact ? 'rounded-[18px]' : 'rounded-[24px]'}`}>
    <div className={`flex items-center gap-3 border-b border-[#e3ebf4] bg-[#f8fbfe] ${compact ? 'px-3 py-2.5' : 'px-4 py-3.5'}`}>
      <span className={`grid shrink-0 place-items-center bg-[#eaf0fb] ${compact ? 'h-9 w-9 rounded-xl text-base' : 'h-11 w-11 rounded-[14px] text-xl'}`} aria-hidden>🍽️</span>
      <div className="min-w-0"><p className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-bold uppercase tracking-[.1em] text-[#456d95]`}>{eyebrow}</p><h2 className={`${compact ? 'text-[16px]' : 'mt-0.5 text-[19px]'} font-bold text-[#172640]`}>What’s for dinner?</h2></div>
    </div>
    <div className={`flex gap-3 ${compact ? 'p-3' : 'p-4'}`}>
      {meal.thumbnail ? <img src={meal.thumbnail} alt={`${meal.name} meal`} className={`${compact ? 'h-20 w-24 rounded-[14px]' : 'h-28 w-[42%] rounded-[18px]'} shrink-0 object-cover`}/> : <div role="img" aria-label={`${meal.name} meal illustration`} className={`${compact ? 'h-20 w-24 rounded-[14px] text-3xl' : 'h-28 w-[42%] rounded-[18px] text-4xl'} grid shrink-0 place-items-center bg-gradient-to-br from-orange-100 to-amber-50`}>🍽️</div>}
      <div className="flex min-w-0 flex-1 flex-col py-0.5">
        <span className={`w-fit rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-wide ${assigned ? 'bg-[#e9f7f4] text-[#177f80]' : 'bg-[#fff2e7] text-[#b86031]'}`}>{assigned ? 'Tonight' : 'Demo from recipe library'}</span>
        <span className={`${compact ? 'mt-1.5 text-[14px]' : 'mt-2 text-[17px]'} font-bold leading-5 text-[#172640]`}>{meal.name}</span>
        {!compact && <span className="mt-1 text-[12px] font-medium leading-5 text-[#71809a]">{assigned ? 'Open ingredients and preparation' : loading ? 'Finding a recipe example…' : 'Tap to preview this recipe'}</span>}
        <span className={`${compact ? 'mt-1' : 'mt-auto pt-2'} text-[12px] font-bold text-[#315f91]`}>View recipe →</span>
      </div>
    </div>
    {!compact && !assigned && <div className="border-t border-[#e6edf5] bg-[#fbfdff] px-4 py-3"><p className="text-[11px] font-medium leading-5 text-[#71809a]">Demo only — this recipe has not been added to {personal ? 'your' : 'Alex’s'} weekly menu.</p></div>}
  </Link>
}

